import { Injectable } from '@angular/core';

export const DEFAULT_RING_COLOR = '#4caf50';
const DEFAULT_RING_RGB = '76,175,80';

@Injectable({ providedIn: 'root' })
export class ImageColorExtractorService {
  extractDominantColor(imageUrl: string): Promise<string> {
    return new Promise<string>((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(DEFAULT_RING_COLOR);
          return;
        }

        const maxSize = 100;
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        canvas.width = Math.floor(img.width * scale);
        canvas.height = Math.floor(img.height * scale);

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        try {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          resolve(this.getDominantColor(imageData.data));
        } catch {
          resolve(DEFAULT_RING_COLOR);
        }
      };

      img.onerror = () => {
        resolve(DEFAULT_RING_COLOR);
      };

      img.src = imageUrl;
    });
  }

  private getDominantColor(imageData: Uint8ClampedArray): string {
    const colorCounts: { [key: string]: number } = {};
    const sampleRate = 10;

    for (let i = 0; i < imageData.length; i += 4 * sampleRate) {
      const r = imageData[i];
      const g = imageData[i + 1];
      const b = imageData[i + 2];
      const a = imageData[i + 3];

      if (a < 128) continue;

      const brightness = (r + g + b) / 3;
      if (brightness < 50 || brightness > 200) continue;

      const reducedR = Math.round(r / 32) * 32;
      const reducedG = Math.round(g / 32) * 32;
      const reducedB = Math.round(b / 32) * 32;

      const colorKey = `${reducedR},${reducedG},${reducedB}`;
      colorCounts[colorKey] = (colorCounts[colorKey] || 0) + 1;
    }

    let dominantColor = DEFAULT_RING_RGB;
    let maxCount = 0;

    for (const [color, count] of Object.entries(colorCounts)) {
      if (count > maxCount) {
        maxCount = count;
        dominantColor = color;
      }
    }

    const [r, g, b] = dominantColor.split(',').map(Number);
    const enhancedColor = this.enhanceColorForNeon(r, g, b);

    return `#${enhancedColor.r.toString(16).padStart(2, '0')}${enhancedColor.g.toString(16).padStart(2, '0')}${enhancedColor.b.toString(16).padStart(2, '0')}`;
  }

  private enhanceColorForNeon(r: number, g: number, b: number): { r: number; g: number; b: number } {
    const hsl = this.rgbToHsl(r, g, b);

    hsl.s = Math.min(1, hsl.s * 1.3);
    hsl.l = Math.max(0.3, Math.min(0.7, hsl.l));

    return this.hslToRgb(hsl.h, hsl.s, hsl.l);
  }

  private rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return { h, s, l };
  }

  private hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
    let r: number, g: number, b: number;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255),
    };
  }
}
